/** @jsxImportSource @emotion/react */

import React, { memo, MouseEvent as ReactMouseEvent } from 'react';
import { useFormButton } from '../../context/FormContext/FormContext';
import { Button, ButtonProps } from '../Button/Button';
import { CSSProp } from '../../../types/emotion';

export interface FormButtonProps extends ButtonProps {
  /**
   * The type of the button
   */
  type: 'submit' | 'reset';
}

/**
 * A button that is used to submit a form, requires a `FormContext` to be present
 */
export const FormButton = memo(function ({
  // memo is necessary to prevent button rerenders which cause onClick not being executed when an onBlur event is triggered at the same time because a FormInput was focused when the button was clicked
  type,
  onClick: customOnClick,
  disabled: customDisabled,
  ...props
}: FormButtonProps &
  ButtonProps &
  Omit<Partial<React.HTMLAttributes<HTMLDivElement>>, keyof FormButtonProps> &
  Omit<Partial<React.HTMLAttributes<HTMLButtonElement>>, keyof FormButtonProps> &
  CSSProp) {
  const { onClick, disabled, clearForm } = useFormButton();

  const handleClick = async (event: ReactMouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>) => {
    if (type === 'submit') {
      await onClick();
    } else {
      clearForm();
    }
    customOnClick?.(event);
  };

  return <Button buttonType={type} onMouseDown={handleClick} disabled={customDisabled || disabled} {...props} />;
});
